import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailDomainDnsZoneState } from '../types/models/MailDomain';
import type { ApiResponseMailDomainDnsZone } from '../types/api/ApiResponseMailDomain';

interface UseMailDomainDnsZoneReturn {
  data: MailDomainDnsZoneState | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Reads the DNS-zone state of a mail domain — `GET /emails/domains/dns-zone?organization_domain_id={id}`.
 *
 * Tells callers whether the domain's zone is managed here and writable (`zone_state === 'configured'`),
 * and carries the `domain_name_id` + live `zone.records` needed to publish a record set. The SPF wizard
 * uses it to decide between offering a Publish action and falling back to manual copy. Gated by `enabled`
 * so it only fires while the wizard is open, and JSend-aware (`body.status`, not `response.ok`).
 */
export const useMailDomainDnsZone = (
  organizationDomainId: number | undefined,
  enabled: boolean,
): UseMailDomainDnsZoneReturn => {
  const [data, setData] = useState<MailDomainDnsZoneState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchZone = useCallback(async () => {
    if (!organizationDomainId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;

      if (body?.status === 'success' && body.data?.dns_zone) {
        setData(body.data.dns_zone);
      } else {
        setError(body?.message ?? 'Failed to load the DNS zone state');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while checking the DNS zone');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, organizationDomainId]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated && organizationDomainId) {
      void fetchZone();
    }
  }, [enabled, auth.isAuthenticated, organizationDomainId, fetchZone]);

  return { data, loading, error, refetch: fetchZone };
};
